import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNostrPublish } from './useNostrPublish';
import { ADMIN_PUBKEY, APPROVED_LISTINGS_D_TAG } from '@/lib/admin';
import { discoveryPool, discoverySignal } from '@/lib/appRelays';

/**
 * Read the admin-approved list of direct-listing pubkeys.
 *
 * Only the admin's kind-30078 event (d = APPROVED_LISTINGS_D_TAG) is trusted;
 * filtered by `authors` at the relay level.
 *
 * Returns a plain array (not a Set) so the result survives the persisted
 * localStorage query cache round-trip.
 */
export function useApprovedListings() {
  return useQuery({
    queryKey: ['nostr', 'approved-listings'],
    queryFn: async (c) => {
      const events = await discoveryPool.query(
        [{
          kinds: [30078],
          authors: [ADMIN_PUBKEY],
          '#d': [APPROVED_LISTINGS_D_TAG],
          limit: 1,
        }],
        { signal: discoverySignal(c.signal) },
      );

      const approved: string[] = [];
      if (events.length > 0) {
        for (const tag of events[0].tags) {
          if (tag[0] === 'p' && tag[1]) approved.push(tag[1].toLowerCase());
        }
      }
      return approved;
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Admin mutation: add or remove a pubkey from the approved-listings set.
 * Republishes the full kind-30078 list (addressable, replaceable).
 */
export function useManageApprovedListings() {
  const { mutateAsync: publishEvent } = useNostrPublish();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (opts: { pubkey: string; approved: boolean }) => {
      // Load the current list so we can add/remove one entry.
      const events = await discoveryPool.query(
        [{
          kinds: [30078],
          authors: [ADMIN_PUBKEY],
          '#d': [APPROVED_LISTINGS_D_TAG],
          limit: 1,
        }],
        { signal: discoverySignal() },
      );

      const current = new Set<string>();
      if (events.length > 0) {
        for (const tag of events[0].tags) {
          if (tag[0] === 'p' && tag[1]) current.add(tag[1].toLowerCase());
        }
      }

      const pk = opts.pubkey.toLowerCase();
      if (opts.approved) current.add(pk);
      else current.delete(pk);

      const tags: string[][] = [
        ['d', APPROVED_LISTINGS_D_TAG],
        ['alt', 'BCH Local approved direct business listings'],
        ...[...current].map((p) => ['p', p]),
      ];

      return publishEvent({ kind: 30078, content: '', tags });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nostr', 'approved-listings'] });
      queryClient.invalidateQueries({ queryKey: ['nostr', 'merchants'] });
    },
  });
}
